import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend
} from 'recharts';
import { format } from 'date-fns';

/**
 * TokenUsageChart - Displays token usage breakdown as a stacked bar chart
 * Shows prompt (input) vs completion (output) tokens per run
 */
const TokenUsageChart = ({ runs, loading }) => {
  // Transform runs data for the chart
  const chartData = useMemo(() => {
    if (!runs || runs.length === 0) return [];
    
    return runs
      .filter(run => run.token_usage?.total_tokens > 0)
      .map(run => ({
        timestamp: new Date(run.created_at).getTime(),
        promptTokens: run.token_usage?.prompt_tokens || 0,
        completionTokens: run.token_usage?.completion_tokens || 0,
        totalTokens: run.token_usage?.total_tokens || 0,
        name: run.project_name || 'Untitled',
        model: run.model
      }))
      .sort((a, b) => a.timestamp - b.timestamp)
      .slice(-15); // Show last 15 runs for readability
  }, [runs]);

  // Calculate totals for summary
  const totals = useMemo(() => {
    if (!chartData || chartData.length === 0) {
      return { prompt: 0, completion: 0, total: 0, promptPercent: '0', completionPercent: '0' };
    }
    
    const prompt = chartData.reduce((sum, d) => sum + d.promptTokens, 0);
    const completion = chartData.reduce((sum, d) => sum + d.completionTokens, 0);
    const total = prompt + completion;
    
    // Avoid division by zero
    const promptPercent = total > 0 ? ((prompt / total) * 100).toFixed(0) : '0';
    const completionPercent = total > 0 ? ((completion / total) * 100).toFixed(0) : '0';
    
    return {
      prompt,
      completion,
      total,
      promptPercent,
      completionPercent
    };
  }, [chartData]);

  // Custom tooltip
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="rounded-lg p-3 shadow-lg bg-bg-secondary/95 border border-primary-500/30 backdrop-blur-sm">
          <p className="text-xs mb-2 text-text-tertiary">
            {format(new Date(data.timestamp), 'MMM d, h:mm a')}
          </p>
          <div className="space-y-1">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-primary-500"></div>
                <span className="text-xs text-text-secondary">Input</span>
              </div>
              <span className="text-xs font-mono text-text-primary">
                {data.promptTokens.toLocaleString()}
              </span>
            </div>
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-cyan-500"></div>
                <span className="text-xs text-text-secondary">Output</span>
              </div>
              <span className="text-xs font-mono text-text-primary">
                {data.completionTokens.toLocaleString()}
              </span>
            </div>
            <div className="pt-1 mt-1 border-t border-primary-500/20">
              <div className="flex items-center justify-between">
                <span className="text-xs text-text-tertiary">Total</span>
                <span className="text-xs font-mono font-semibold text-primary-500">
                  {data.totalTokens.toLocaleString()}
                </span>
              </div>
            </div>
          </div>
          <p className="text-xs mt-2 truncate text-text-tertiary max-w-[150px]">
            {data.name}
          </p>
        </div>
      );
    }
    return null;
  };
  CustomTooltip.propTypes = {
    active: PropTypes.bool,
    payload: PropTypes.array
  };

  // Custom legend
  const CustomLegend = () => {
    return (
      <div className="flex justify-center gap-6 mt-2">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded bg-primary-500"></div>
          <span className="text-xs text-text-secondary">
            Input ({totals.promptPercent}%)
          </span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded bg-cyan-500"></div>
          <span className="text-xs text-text-secondary">
            Output ({totals.completionPercent}%)
          </span>
        </div>
      </div>
    );
  };

  // Format Y-axis (abbreviate large numbers)
  const formatYAxis = (value) => {
    if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `${(value / 1000).toFixed(0)}K`;
    return value;
  };

  if (loading && (!runs || runs.length === 0)) {
    return (
      <div className="rounded-lg p-6 bg-bg-secondary/60 border border-border-secondary">
        <div className="h-4 w-32 rounded mb-4 animate-pulse bg-success-50"></div>
        <div className="h-[200px] rounded animate-pulse bg-primary-500/5"></div>
      </div>
    );
  }

  if (!chartData || chartData.length === 0) {
    return (
      <div className="rounded-lg p-6 bg-bg-secondary/60 border border-border-secondary">
        <h3 className="text-sm font-semibold mb-4 text-text-primary">
          Token Usage
        </h3>
        <div className="h-[200px] flex items-center justify-center">
          <p className="text-sm text-text-tertiary">No token data available</p>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-lg p-6 bg-bg-secondary/60 border border-border-secondary transition-all duration-200 hover:border-primary-500/25">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-text-primary">
          Token Usage
        </h3>
        <span className="text-xs font-mono text-primary-500">
          {totals.total.toLocaleString()} total
        </span>
      </div>
      
      <div className="h-[200px]">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={chartData}
            margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
          >
            <CartesianGrid 
              strokeDasharray="3 3" 
              stroke="rgba(50, 205, 50, 0.1)" 
              vertical={false}
            />
            <XAxis 
              dataKey="timestamp"
              tickFormatter={(ts) => format(new Date(ts), 'h:mm')}
              stroke="#6B8E6B"
              fontSize={10}
              tickLine={false}
              axisLine={{ stroke: 'rgba(50, 205, 50, 0.2)' }}
              tick={{ fill: '#6B8E6B' }}
            />
            <YAxis 
              tickFormatter={formatYAxis}
              stroke="#6B8E6B"
              fontSize={10}
              tickLine={false}
              axisLine={{ stroke: 'rgba(50, 205, 50, 0.2)' }}
              tick={{ fill: '#6B8E6B' }}
              width={40}
            />
            <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(50, 205, 50, 0.08)' }} />
            <Legend content={<CustomLegend />} />
            <Bar 
              dataKey="promptTokens" 
              stackId="tokens"
              fill="#32CD32"
              name="Input Tokens"
            />
            <Bar 
              dataKey="completionTokens" 
              stackId="tokens"
              fill="#00BCD4"
              name="Output Tokens"
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

TokenUsageChart.propTypes = {
  runs: PropTypes.arrayOf(PropTypes.shape({
    token_usage: PropTypes.object,
    created_at: PropTypes.string,
    run_name: PropTypes.string
  })),
  loading: PropTypes.bool
};

export default TokenUsageChart;
